import { type Accessor, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { createExtendedPowerSyncStatus } from "@bso/shared/solid";

/**
 * Resolution of a detail entity in a local-first app.
 *
 * The problem: opening an entity detail by id (organization, etc.),
 * or opening the reader at a reference, the row/resource may:
 *   - exist locally → show it;
 *   - **not be synced yet** (initial sync in progress) → keep loading;
 *   - **not exist** (deleted / invalid id) → "not found";
 *   - **not be accessible** (PowerSync sync rules do NOT pull rows without permission,
 *     so from the client "no permission" ≈ "absent after sync") → "no access".
 *
 * Without this handling, an absent entity leaves the screen **loading forever**.
 * Here we distinguish the states to give correct feedback.
 */
type EntityResolution = "loading" | "ready" | "not-found" | "offline";

export interface ResolutionInputs {
  /** The entity is present locally. */
  hasEntity: boolean;
  /** The query has not resolved yet (no settled result yet). */
  isPending: boolean;
  /** PowerSync finished the initial sync. */
  hasSynced: boolean;
  /** There is a network connection. */
  isOnline: boolean;
  /** The safety net expired while we waited (prevents loading forever). */
  timedOut: boolean;
}

/**
 * PURE, testable core of the state machine (no reactivity).
 *
 * - present → `ready`
 * - query pending → `loading`
 * - settled and absent:
 *     - already synced (or timeout expired) → `not-found` (does not exist / no access)
 *     - offline and never synced → `offline` (cannot tell yet)
 *     - online but still no initial sync → `loading` (wait for it to arrive)
 */
export function resolveEntityState(input: ResolutionInputs): EntityResolution {
  if (input.hasEntity) {
    return "ready";
  }
  if (input.isPending) {
    return "loading";
  }
  if (input.hasSynced || input.timedOut) {
    return "not-found";
  }
  if (!input.isOnline) {
    return "offline";
  }
  return "loading";
}

const DEFAULT_RESOLUTION_TIMEOUT_MS = 8000;

interface EntityResolutionOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  /** `true` when the entity is loaded locally. */
  hasEntity: Accessor<boolean>;
  /** `true` while the query has no settled result. */
  isPending: Accessor<boolean>;
  /** Safety net: ms waiting (online, no sync) before declaring not-found. */
  timeoutMs?: number;
}

/**
 * Reactive wrapper: combines the entity query with PowerSync's sync state and
 * a timeout safety net. Returns the resolution state.
 */
export function createEntityResolution(
  options: EntityResolutionOptions,
): Accessor<EntityResolution> {
  const status = createExtendedPowerSyncStatus(options.db);
  const [timedOut, setTimedOut] = createSignal(false);

  // Start the timeout only during the real waiting window (absent, online,
  // no initial sync). If the entity arrives or the state changes, it is cancelled/restarted.
  createEffect(() => {
    const waiting =
      !options.hasEntity() && !options.isPending() && !status().hasSynced && status().isOnline;
    if (!waiting) {
      setTimedOut(false);
      return;
    }
    const id = setTimeout(() => {
      setTimedOut(true);
    }, options.timeoutMs ?? DEFAULT_RESOLUTION_TIMEOUT_MS);
    onCleanup(() => {
      clearTimeout(id);
    });
  });

  const resolution = createMemo(() =>
    resolveEntityState({
      hasEntity: options.hasEntity(),
      isPending: options.isPending(),
      hasSynced: status().hasSynced,
      isOnline: status().isOnline,
      timedOut: timedOut(),
    }),
  );
  return resolution;
}
